import {
  Type,
  Hash,
  Calendar,
  List,
  ListChecks,
  CheckSquare,
  Users,
  Mail,
  Link,
  Phone,
  DollarSign,
  Paperclip,
  PenTool,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { FIELD_TYPE } from '../types';
import type { FieldType } from '../types';

export interface FieldRegistryEntry {
  icon: LucideIcon;
  labelKey: string;
  colorClass: string;
}

export const FIELD_REGISTRY: Record<FieldType, FieldRegistryEntry> = {
  [FIELD_TYPE.Text]: {
    icon: Type,
    labelKey: 'customFields.fieldTypes.text',
    colorClass: 'bg-blue-100 text-blue-700',
  },
  [FIELD_TYPE.Number]: {
    icon: Hash,
    labelKey: 'customFields.fieldTypes.number',
    colorClass: 'bg-green-100 text-green-700',
  },
  [FIELD_TYPE.Date]: {
    icon: Calendar,
    labelKey: 'customFields.fieldTypes.date',
    colorClass: 'bg-orange-100 text-orange-700',
  },
  [FIELD_TYPE.Select]: {
    icon: List,
    labelKey: 'customFields.fieldTypes.select',
    colorClass: 'bg-purple-100 text-purple-700',
  },
  [FIELD_TYPE.MultiSelect]: {
    icon: ListChecks,
    labelKey: 'customFields.fieldTypes.multiSelect',
    colorClass: 'bg-violet-100 text-violet-700',
  },
  [FIELD_TYPE.Checkbox]: {
    icon: CheckSquare,
    labelKey: 'customFields.fieldTypes.checkbox',
    colorClass: 'bg-teal-100 text-teal-700',
  },
  [FIELD_TYPE.Users]: {
    icon: Users,
    labelKey: 'customFields.fieldTypes.users',
    colorClass: 'bg-rose-100 text-rose-700',
  },
  [FIELD_TYPE.Email]: {
    icon: Mail,
    labelKey: 'customFields.fieldTypes.email',
    colorClass: 'bg-cyan-100 text-cyan-700',
  },
  [FIELD_TYPE.Url]: {
    icon: Link,
    labelKey: 'customFields.fieldTypes.url',
    colorClass: 'bg-sky-100 text-sky-700',
  },
  [FIELD_TYPE.Phone]: {
    icon: Phone,
    labelKey: 'customFields.fieldTypes.phone',
    colorClass: 'bg-lime-100 text-lime-700',
  },
  [FIELD_TYPE.Currency]: {
    icon: DollarSign,
    labelKey: 'customFields.fieldTypes.currency',
    colorClass: 'bg-emerald-100 text-emerald-700',
  },
  [FIELD_TYPE.Files]: {
    icon: Paperclip,
    labelKey: 'customFields.fieldTypes.files',
    colorClass: 'bg-slate-100 text-slate-700',
  },
  [FIELD_TYPE.Signature]: {
    icon: PenTool,
    labelKey: 'customFields.fieldTypes.signature',
    colorClass: 'bg-fuchsia-100 text-fuchsia-700',
  },
};

/** `Files` and `Signature` values are backend-constructed — never writable via the generic PUT. */
export function isFileBackedFieldType(fieldType: FieldType): boolean {
  return fieldType === FIELD_TYPE.Files || fieldType === FIELD_TYPE.Signature;
}

export function getFieldRegistryEntry(fieldType: FieldType): FieldRegistryEntry {
  return FIELD_REGISTRY[fieldType];
}

/** Resolves a fieldType string from the API response to a FieldType number. */
export function resolveFieldType(fieldTypeStr: string | null): FieldType | null {
  if (!fieldTypeStr) return null;
  const entry = Object.entries(FIELD_TYPE).find(
    ([key]) => key.toLowerCase() === fieldTypeStr.toLowerCase(),
  );
  return entry ? (entry[1] as FieldType) : null;
}
